/**
 * Venttra - Destinations collection schema and Seed Cities action
 * Matches Firestore schema in services/content-engine/lib/firestore.ts
 */
import React, { useState } from "react";
import { editorOrAdminPermissions } from "../permissions";
import {
  buildCollection,
  CollectionActionsProps,
  useSnackbarController,
} from "@firecms/core";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, TranslateIcon, AutoFixHighIcon, AddPhotoAlternateIcon, RefreshIcon, RestoreIcon } from "@firecms/ui";
import { getApps } from "firebase/app";
import { doc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";
import { createDestinationsLocalizationReviewView } from "../components/LocalizationReviewView";
import {
  createDestinationsImagesView,
  destinationImagesProperty,
  destinationKeywordsProperty,
} from "../components/DestinationImagesView";
import { CONTENT_LOCALES, type ContentLocale, LOCALE_LABELS } from "../constants";
import { collectDestinationCompletenessIssues } from "../lib/completenessValidation";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import { DESTINATION_LEVELS } from "../lib/destinationLevels";
import {
  applyPublishedSnapshot,
  hasPublishedSnapshot,
} from "../lib/publishedSnapshot";
import { createStatusPreSave } from "../lib/statusPreSave";
import { ADMIN_TIER_ENUM } from "../lib/adminTier";

const V2_VOICE_HINT =
  "Brand voice: Empathetic, Community-Driven & Conscious, Effortless. Lead with cooperative community value. Avoid jargon and preachy sustainability.";
const V2_TERMINOLOGY_HINT =
  "Use official member tiers only when needed (Traveler, Pioneer, Navigator, Vanguard). Never use VIP or Influencer.";

/** Generates localized content property definitions for a locale */
function createLocalizedContentProperties(locale: ContentLocale) {
  return {
    name: LOCALE_LABELS[locale],
    dataType: "map" as const,
    properties: {
      headline: {
        name: "Headline",
        dataType: "string" as const,
        description:
          `Short line under destination name (dek). Tease the experience, not a feature list. ${V2_VOICE_HINT} ${V2_TERMINOLOGY_HINT}`,
      },
      intro: {
        name: "Intro",
        dataType: "string" as const,
        markdown: true,
        description:
          `Narrative introduction in 2-3 paragraphs. Prioritize shared traveler insight and local connection. ${V2_VOICE_HINT}`,
      },
      stats: {
        name: "Stats",
        dataType: "array" as const,
        of: { dataType: "string" as const },
        description:
          "Exactly 3 strings in 'Label: value' format. Keep facts clear and useful for planning decisions.",
      },
      faqs: {
        name: "FAQs",
        dataType: "array" as const,
        description:
          "Helpful Q&A in plain language. Keep answers concise and practical, with encouraging tone.",
        of: {
          dataType: "map" as const,
          properties: {
            q: { name: "Question", dataType: "string" as const },
            a: { name: "Answer", dataType: "string" as const, markdown: true },
          },
        },
      },
      tldr: {
        name: "At a glance",
        dataType: "array" as const,
        of: { dataType: "string" as const },
        description:
          "3–5 concise decision/intent bullets for skimmers (why go, who for, practical takeaway). Not 'Label: value' stats. Frontend heading is 'At a glance', not TLDR.",
      },
      page_title: {
        name: "Page title",
        dataType: "string" as const,
        description:
          "SEO/display page title (may differ from place name). Not the visible H1 place name.",
      },
      meta_title: {
        name: "Meta title",
        dataType: "string" as const,
        description:
          "HTML <title> before brand suffix (~50–60 chars). Site appends '| Venttra'.",
      },
      meta_description: {
        name: "Meta description",
        dataType: "string" as const,
        description: "Meta description (~150–160 chars) for SERPs and AI extraction.",
      },
    },
  };
}

/** Content Engine seed response */
interface SeedResult {
  created: number;
  skipped: number;
  updated: number;
  tourist_regions?: number;
  tourist_regions_existing?: number;
  islands?: number;
  islands_existing?: number;
  live_demoted_to_draft?: number;
  errors: string[];
}

/** Seed Cities action component - appears in destinations collection toolbar */
function SeedCitiesAction(props: CollectionActionsProps) {
  const { selectionController, context } = props;
  const snackbarController = useSnackbarController();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [populationThreshold, setPopulationThreshold] = useState(100_000);
  const [parentId, setParentId] = useState("");

  const selectedEntities = selectionController.selectedEntities ?? [];
  const selectedCountry = selectedEntities.find(
    (e) => (e.values as { level?: string }).level === "country"
  );

  const handleOpen = () => {
    if (selectedCountry) {
      const v = selectedCountry.values as { country_code?: string };
      setCountryCode(v.country_code ?? "");
      setParentId(selectedCountry.id);
    } else {
      setCountryCode("");
      setParentId("");
      setPopulationThreshold(100_000);
    }
    setOpen(true);
  };

  const handleSeed = async () => {
    const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
    if (!contentEngineUrl) {
      snackbarController.open({
        type: "error",
        message: "Content Engine URL not configured (VITE_CONTENT_ENGINE_URL)",
      });
      return;
    }
    if (!countryCode || countryCode.length !== 2) {
      snackbarController.open({
        type: "error",
        message: "Country code must be a 2-letter ISO code (e.g. FR, DE)",
      });
      return;
    }

    setLoading(true);
    try {
      const idToken = await context.authController.getAuthToken();
      if (!idToken?.trim()) {
        snackbarController.open({
          type: "error",
          message: "Not authenticated. Please sign out and sign in again, then try Seed again.",
        });
        setLoading(false);
        return;
      }
      const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/seed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          country_code: countryCode.toUpperCase(),
          population_threshold: populationThreshold,
          parent_id: parentId || null,
        }),
      });

      const data = (await res.json()) as SeedResult | { error: string };
      if (!res.ok) {
        const err = "error" in data ? data.error : "Seed failed";
        snackbarController.open({ type: "error", message: err });
        return;
      }

      const result = data as SeedResult;
      const parts = [
        `${result.created} created`,
        `${result.skipped} skipped`,
        `${result.updated} updated`,
      ];
      if ((result.tourist_regions ?? 0) + (result.tourist_regions_existing ?? 0) > 0) {
        parts.push(
          `tourist regions ${result.tourist_regions ?? 0} new / ${result.tourist_regions_existing ?? 0} existing`,
        );
      }
      if ((result.islands ?? 0) + (result.islands_existing ?? 0) > 0) {
        parts.push(
          `islands ${result.islands ?? 0} new / ${result.islands_existing ?? 0} existing`,
        );
      }
      const errDetail =
        result.errors.length > 0
          ? ` · ${result.errors.length} warning(s): ${result.errors.slice(0, 3).join("; ")}`
          : "";
      snackbarController.open({
        type: result.errors.length ? "warning" : "success",
        message: `Seed complete: ${parts.join(", ")}. New islands/tourist regions are Draft — filter Destinations by status Draft or search by name (e.g. Mallorca, Costa Brava).${errDetail}`,
      });
      setOpen(false);
    } catch (err) {
      snackbarController.open({
        type: "error",
        message: err instanceof Error ? err.message : "Seed request failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="medium"
        onClick={handleOpen}
        disabled={loading}
      >
        Seed Cities
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTitle>Seed Cities from GeoNames</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-gray-600 m-0">
              Also creates curated tourist regions (e.g. Costa Brava) and islands (e.g. Mallorca)
              as Draft administrative divisions — independent of the population threshold.
            </p>
            <TextField
              label="Country Code (ISO 3166 alpha2)"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              placeholder="e.g. FR, DE"
            />
            <TextField
              label="Population Threshold"
              type="number"
              value={String(populationThreshold)}
              onChange={(e) =>
                setPopulationThreshold(
                  parseInt(e.target.value, 10) || 100_000
                )
              }
            />
            <TextField
              label="Parent ID (optional - Firestore doc ID of country)"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              placeholder="Leave empty to auto-create continent/country"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="filled"
            color="primary"
            onClick={handleSeed}
            disabled={loading || !countryCode}
          >
            {loading ? "Seeding…" : "Seed"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function DestinationsToolbarActions(props: CollectionActionsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 venttra-collection-toolbar-actions">
      <SeedCitiesAction {...props} />
    </div>
  );
}

const destinationStatusPreSave = createStatusPreSave({
  collectCompleteness: collectDestinationCompletenessIssues,
  entityLabel: "destination",
});

export const destinationsCollection = buildCollection({
  id: "destinations",
  name: "Destinations",
  singularName: "Destination",
  path: "destinations",
  icon: "TravelExplore",
  group: "Content",
  Actions: DestinationsToolbarActions,
  permissions: editorOrAdminPermissions,
  entityViews: [createDestinationsLocalizationReviewView(), createDestinationsImagesView()],
  propertiesOrder: [
    "name",
    "name_localized",
    "status",
    "admin_tier",
    "level",
    "type_id",
    "type_code",
    "feature_code",
    "admin_code",
    "catalog_id",
    "parent_id",
    "country_code",
    "geoname_id",
    "wikidata_id",
    "sitelinks",
    "population",
    "content",
    "research_brief",
    "research_draft",
    "research_challenge",
    "created_at",
    "updated_at",
  ],
  entityActions: [
    {
      name: "Fill empty content",
      icon: <AutoFixHighIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/run-destination-expert`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, fill_empty: true }),
          });
          const data = (await res.json()) as { ok?: boolean; skipped?: boolean; error?: string };
          if (res.ok) {
            snackbar.open({
              type: data.skipped ? "info" : "success",
              message: data.skipped
                ? "Nothing empty to fill — content fields are already complete"
                : "Empty content fields filled. Use Localization Review for field-by-field generate.",
            });
          } else {
            snackbar.open({ type: "error", message: data.error ?? "Fill empty content failed" });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
        }
      },
    },
    {
      name: "Generate all English",
      icon: <AutoFixHighIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/run-destination-expert`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({
              document_id: entity.id,
              force: true,
              refetch_images: false,
            }),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            error?: string;
            skipped?: boolean;
            content_changed?: boolean;
            changed_fields?: string[];
          };
          if (res.ok) {
            const changed = data.content_changed === true;
            snackbar.open({
              type: changed ? "success" : "info",
              message: data.skipped
                ? "Skipped — English content already present. Use Fill empty or check Localization Review."
                : changed
                ? `Regenerated English (${(data.changed_fields ?? []).join(", ") || "fields updated"}). Translations may show Stale — open Localization Review and Refresh status, then re-translate. Reload this entity to see EN text.`
                : "Generate finished but English text is unchanged (same hashes). Translations correctly stay Current. Try again or edit fields manually.",
            });
          } else {
            snackbar.open({ type: "error", message: data.error ?? "Generate all English failed" });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
        }
      },
    },
    {
      name: "Generate all English + images",
      icon: <AddPhotoAlternateIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/run-destination-expert`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({
              document_id: entity.id,
              force: true,
              refetch_images: true,
            }),
          });
          const data = (await res.json()) as {
            ok?: boolean;
            error?: string;
            skipped?: boolean;
            content_changed?: boolean;
            changed_fields?: string[];
          };
          if (res.ok) {
            const changed = data.content_changed === true;
            snackbar.open({
              type: changed ? "success" : "info",
              message: data.skipped
                ? "Skipped — English content already present."
                : changed
                ? `Regenerated English + images (${(data.changed_fields ?? []).join(", ") || "fields updated"}). Reload entity / Images tab. Check Localization Review for Stale translations.`
                : "Generate finished but English text is unchanged. Translations correctly stay Current.",
            });
          } else {
            snackbar.open({
              type: "error",
              message: data.error ?? "Generate all English + images failed",
            });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
        }
      },
    },
    {
      name: "Re-fetch images",
      icon: <RefreshIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/re-fetch-images`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, mode: "replace" }),
          });
          const data = (await res.json()) as { ok?: boolean; message?: string; error?: string };
          if (res.ok && data.ok) {
            snackbar.open({
              type: "success",
              message: `${data.message ?? "Images re-fetched"}. Open the Images tab (or reload) to see the new gallery.`,
            });
          } else {
            snackbar.open({ type: "error", message: data.error ?? "Re-fetch images failed" });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
        }
      },
    },
    {
      name: "Fetch additional images",
      icon: <AddPhotoAlternateIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/re-fetch-images`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, mode: "append" }),
          });
          const data = (await res.json()) as { ok?: boolean; message?: string; error?: string };
          if (res.ok && data.ok) {
            snackbar.open({ type: "success", message: data.message ?? "Additional images fetched" });
          } else {
            snackbar.open({ type: "error", message: data.error ?? "Fetch additional images failed" });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Request failed" });
        }
      },
    },
    {
      name: "Translate all",
      icon: <TranslateIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          const res = await fetch(`${contentEngineUrl.replace(/\/$/, "")}/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
            body: JSON.stringify({ document_id: entity.id, mode: "force" }),
          });
          const data = (await res.json()) as { ok?: boolean; message?: string; error?: string; skipped?: boolean };
          if (res.ok && data.ok) {
            snackbar.open({
              type: data.skipped ? "info" : "success",
              message: data.message
                ? `${data.message} Field-by-field / stale actions: Localization Review tab.`
                : "Translation complete. Field-by-field / stale actions: Localization Review tab.",
            });
          } else {
            snackbar.open({ type: "error", message: data.error ?? "Translation failed" });
          }
        } catch (err) {
          snackbar.open({ type: "error", message: err instanceof Error ? err.message : "Translation request failed" });
        }
      },
    },
    {
      name: "Reseed hotels",
      icon: <RefreshIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        if (entity.values?.level !== "city") {
          snackbar.open({
            type: "info",
            message: "Hotel seed only runs for city destinations.",
          });
          return;
        }
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          snackbar.open({ type: "info", message: "Seeding hotels from LiteAPI… this can take a minute." });
          const res = await fetch(
            `${contentEngineUrl.replace(/\/$/, "")}/api/hotels/seed-for-destination`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({ destinationId: entity.id }),
            },
          );
          const data = (await res.json()) as {
            created?: number;
            updated?: number;
            missing?: number;
            blurbs_regenerated?: number;
            status_demoted?: number;
            skipped?: string;
            error?: string;
          };
          if (!res.ok) {
            snackbar.open({ type: "error", message: data.error ?? "Hotel reseed failed" });
            return;
          }
          if (data.skipped) {
            snackbar.open({
              type: "info",
              message: `Hotel seed skipped (${data.skipped}).`,
            });
            return;
          }
          snackbar.open({
            type: "success",
            message:
              `Hotels reseeded: ${data.created ?? 0} created, ${data.updated ?? 0} updated, ${data.missing ?? 0} marked missing.` +
              (data.status_demoted
                ? ` ${data.status_demoted} live hotel(s) → Live · Draft (review → Live · Ready → Global Publish).`
                : "") +
              ` Unchanged blurbs stay as-is — use “Reseed hotels + regenerate blurbs” to rewrite Expert copy.`,
          });
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Hotel reseed request failed",
          });
        }
      },
    },
    {
      name: "Reseed hotels + regenerate blurbs",
      icon: <AutoFixHighIcon />,
      onClick: async ({ entity, context }) => {
        const contentEngineUrl = import.meta.env.VITE_CONTENT_ENGINE_URL;
        if (!contentEngineUrl || !entity?.id) return;
        const snackbar = context.snackbarController;
        if (entity.values?.level !== "city") {
          snackbar.open({
            type: "info",
            message: "Hotel seed only runs for city destinations.",
          });
          return;
        }
        try {
          const idToken = await context.authController.getAuthToken();
          if (!idToken?.trim()) {
            snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
            return;
          }
          snackbar.open({
            type: "info",
            message: "Reseeding hotels and regenerating unlocked blurbs… this can take several minutes.",
          });
          const res = await fetch(
            `${contentEngineUrl.replace(/\/$/, "")}/api/hotels/seed-for-destination`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({ destinationId: entity.id, forceBlurbs: true }),
            },
          );
          const data = (await res.json()) as {
            created?: number;
            updated?: number;
            missing?: number;
            blurbs_regenerated?: number;
            blurbs_skipped_locked?: number;
            status_demoted?: number;
            skipped?: string;
            error?: string;
          };
          if (!res.ok) {
            snackbar.open({ type: "error", message: data.error ?? "Hotel reseed failed" });
            return;
          }
          if (data.skipped) {
            snackbar.open({
              type: "info",
              message: `Hotel seed skipped (${data.skipped}).`,
            });
            return;
          }
          snackbar.open({
            type: "success",
            message:
              `Hotels: ${data.created ?? 0} created, ${data.updated ?? 0} updated.` +
              ` Blurbs regenerated: ${data.blurbs_regenerated ?? 0}` +
              (data.blurbs_skipped_locked
                ? ` (${data.blurbs_skipped_locked} skipped — Blurb locked).`
                : ".") +
              (data.status_demoted
                ? ` ${data.status_demoted} live hotel(s) → Live · Draft.`
                : "") +
              ` Review Hotels with needs_review, then Live · Ready → Global Publish.`,
          });
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Hotel reseed request failed",
          });
        }
      },
    },
    {
      name: "Revert to live",
      icon: <RestoreIcon />,
      onClick: async ({ entity, context }) => {
        if (!entity?.id) return;
        const snackbar = context.snackbarController;
        const values = (entity.values ?? {}) as Record<string, unknown>;
        if (!hasPublishedSnapshot(values)) {
          snackbar.open({
            type: "info",
            message: "No live snapshot yet. Publish once to enable revert.",
          });
          return;
        }
        const status = values.status;
        if (status !== "published_draft" && status !== "published_ready") {
          snackbar.open({
            type: "info",
            message: "Revert is for Live · Draft / Live · Ready items with unpublished edits.",
          });
          return;
        }
        const app = getApps()[0];
        if (!app) {
          snackbar.open({ type: "error", message: "Firebase is not initialized." });
          return;
        }
        try {
          const restored = applyPublishedSnapshot(
            values,
            values.published_snapshot as Record<string, unknown>,
          );
          const { status: _s, updated_at: _u, created_at: _c, ...fields } = restored;
          await updateDoc(doc(getFirestore(app), entity.path, entity.id), {
            ...fields,
            status: "published",
            updated_at: serverTimestamp(),
          });
          snackbar.open({
            type: "success",
            message: "Reverted to the live version. Reload the entity to see it.",
          });
        } catch (err) {
          snackbar.open({
            type: "error",
            message: err instanceof Error ? err.message : "Revert failed",
          });
        }
      },
    },
  ],
  callbacks: {
    onPreSave: ({ values, previousValues }) => {
      const v = values as Record<string, unknown>;
      if (v.level === "administrative_division") {
        const nl = v.name_localized as Record<string, string> | undefined;
        if (nl?.en?.trim()) {
          v.name = nl.en.trim();
        }
      }
      // Preserve engine-written provenance if the form did not load/edit it.
      const prevMeta = (previousValues as Record<string, unknown> | undefined)?.translation_meta;
      if (prevMeta != null && (v.translation_meta == null || v.translation_meta === undefined)) {
        v.translation_meta = prevMeta;
      }
      return destinationStatusPreSave({
        values: v,
        previousValues: previousValues as Record<string, unknown> | undefined,
      });
    },
  },
  properties: {
    name: {
      name: "Name",
      dataType: "string",
      validation: { required: true },
    },
    name_localized: {
      name: "Name (localized)",
      dataType: "map",
      description: "Localized names for URLs and UI. English is also used as the main Name for admin divisions.",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const },
        ])
      ),
    },
    type_id: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Admin Division Type",
      dataType: "reference" as const,
      path: "admin_division_types",
      description:
        "Type (Canton, State, Region, Island, Tourist region, etc.). Set by seed/fix-hierarchy from country.",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : false,
    }),
    type_code: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Type code",
      dataType: "string" as const,
      description:
        "Denormalized type code (canton, island, tourist_region, …) for Explore dual browse.",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : true,
    }),
    admin_tier: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Admin tier",
      dataType: "string" as const,
      enumValues: ADMIN_TIER_ENUM,
      description:
        "Primary = region/archipelago under country; Nested = island under archipelago; Tourist = curated region under country. Set by seed/fix-hierarchy.",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : true,
    }),
    feature_code: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Feature code",
      dataType: "string" as const,
      description: "GeoNames feature code (ADM1, ADM2, ISL, ISLS).",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : true,
    }),
    admin_code: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Admin code",
      dataType: "string" as const,
      description: "GeoNames admin code (e.g. 07 for Balears, CN for Canarias).",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : true,
    }),
    catalog_id: ({ values }: { values: Record<string, unknown> }) => ({
      name: "Catalog ID",
      dataType: "string" as const,
      description: "Stable curated catalog id for tourist regions (e.g. es-costa-brava).",
      disabled: (values as { level?: string }).level !== "administrative_division" ? { hidden: true } : false,
    }),
    geoname_id: {
      name: "GeoNames ID",
      dataType: "number",
      description: "GeoNames ID for deduplication",
    },
    wikidata_id: {
      name: "Wikidata ID",
      dataType: "string",
      description: "Wikidata Q-id (e.g. Q78). Filled from GeoNames via P1566; used for localized names and image captions.",
    },
    sitelinks: {
      name: "Wikipedia sitelinks",
      dataType: "map",
      keyValue: true,
      description: "Wikipedia page titles per locale (en/de/fr/it).",
    },
    parent_id: {
      name: "Parent Destination",
      dataType: "reference",
      path: "destinations",
      description:
        "Parent destination (continent, country, or administrative division). Islands nest under ADM1; tourist regions parent to country.",
    },
    level: {
      name: "Level",
      dataType: "string",
      enumValues: DESTINATION_LEVELS,
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description:
        "Draft / Ready for first publish. Live = on site and in sync. Live · Draft = on site with unpublished CMS edits. Live · Ready = on site and queued for the next Publish. Use Revert to live to discard CMS edits.",
    },
    content: {
      name: "Content",
      dataType: "map",
      description:
        "Localized content: { en/de/fr/it }. Follow V2 voice and terminology guardrails in every locale.",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [locale, createLocalizedContentProperties(locale)])
      ),
    },
    research_brief: {
      name: "Final research brief (internal)",
      dataType: "string",
      markdown: true,
      description:
        "EN-only factual spine after challenge/revise — used for generation. Not published on the live site. Not translated.",
    },
    research_draft: {
      name: "Research draft (internal)",
      dataType: "string",
      markdown: true,
      description:
        "EN-only first-pass draft brief (debugging). Not published on the live site. Not translated.",
    },
    research_challenge: {
      name: "Challenge notes (internal)",
      dataType: "string",
      markdown: true,
      description:
        "EN-only critique notes from the challenge pass. Draft audit only — not published.",
    },
    keywords: {
      ...destinationKeywordsProperty,
      disabled: { hidden: true, clearOnDisabled: false },
    },
    images: {
      ...destinationImagesProperty,
      disabled: { hidden: true, clearOnDisabled: false },
    },
    translation_meta: {
      name: "Translation provenance",
      dataType: "map",
      keyValue: true,
      disabled: { hidden: true, clearOnDisabled: false },
      description:
        "Internal EN source hashes and validation notes used for translation staleness (set by content-engine).",
    },
    published_snapshot: {
      name: "Live snapshot",
      dataType: "map",
      keyValue: true,
      disabled: { hidden: true, clearOnDisabled: false },
      description:
        "Last live content. Used to keep the live site stable while editing, and for Revert to live.",
    },
    published_at: {
      name: "Last published at",
      dataType: "date",
      disabled: { hidden: true, clearOnDisabled: false },
    },
    country_code: {
      name: "Country Code",
      dataType: "string",
      description: "ISO 3166 alpha2",
    },
    population: {
      name: "Population",
      dataType: "number",
    },
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});

/** Primary administrative divisions (regions/archipelagos under country). */
export const primaryDivisionsCollection = buildCollection({
  ...(destinationsCollection as typeof destinationsCollection),
  id: "destinations_primary",
  name: "Primary divisions",
  initialFilter: { admin_tier: ["==", "primary"] },
});

/** Nested islands and ADM2 under another admin division. */
export const nestedDivisionsCollection = buildCollection({
  ...(destinationsCollection as typeof destinationsCollection),
  id: "destinations_nested",
  name: "Nested divisions (islands)",
  initialFilter: { admin_tier: ["==", "nested"] },
});
